import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

/**
 * Imperative confirmation dialog built on MUI, replacing antd's `Modal.confirm`.
 *
 * Accepts the same config shape the codebase already passed to `Modal.confirm`
 * (`title`, `content`, `onOk`, `onCancel`, `okText`, `cancelText`,
 * `okButtonProps: { danger }`, `width`, `centered`). `onOk` may be async: the
 * OK button shows a disabled/loading state until it settles, the dialog closes
 * on resolve and stays open on reject — matching antd's behaviour.
 */
export interface ConfirmConfig {
    title?: React.ReactNode;
    content?: React.ReactNode;
    okText?: React.ReactNode;
    cancelText?: React.ReactNode;
    okButtonProps?: { danger?: boolean } & Record<string, any>;
    cancelButtonProps?: Record<string, any>;
    width?: number | string;
    centered?: boolean;
    onOk?: () => any;
    onCancel?: () => any;
}

const theme = createTheme();

const ConfirmModal = ({
    config,
    onClosed,
}: {
    config: ConfirmConfig;
    onClosed: () => void;
}) => {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);

    const handleOk = async () => {
        try {
            setLoading(true);
            await config.onOk?.();
            setOpen(false);
        } catch (err) {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        config.onCancel?.();
        setOpen(false);
    };

    const { danger, ...restOkProps } = config.okButtonProps || {};

    return (
        <Dialog
            open={open}
            onClose={(_e, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    handleCancel();
                }
            }}
            slotProps={{
                transition: { onExited: onClosed },

                paper: config.width
                    ? { sx: { width: config.width, maxWidth: config.width } }
                    : undefined
            }}>
            {config.title != null && <DialogTitle>{config.title}</DialogTitle>}
            {config.content != null && (
                <DialogContent>
                    <DialogContentText component="div">
                        {config.content}
                    </DialogContentText>
                </DialogContent>
            )}
            <DialogActions>
                <Button onClick={handleCancel} {...(config.cancelButtonProps || {})}>
                    {config.cancelText ?? "Cancel"}
                </Button>
                <Button
                    onClick={handleOk}
                    autoFocus
                    disabled={loading}
                    variant="contained"
                    style={{ boxShadow: "none" }}
                    color={danger ? "error" : "primary"}
                    {...restOkProps}
                >
                    {config.okText ?? "OK"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export function confirmDialog(config: ConfirmConfig): void {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);

    const cleanup = () => {
        root.unmount();
        if (host.parentNode) {
            host.parentNode.removeChild(host);
        }
    };

    root.render(
        <ThemeProvider theme={theme}>
            <ConfirmModal config={config} onClosed={cleanup} />
        </ThemeProvider>
    );
}

export default confirmDialog;
